import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { Tile } from '@/components/primitives/Tile';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTopAccounts } from '@/hooks/queries/useTopAccounts';
import { formatNumber, formatPeriodRange } from '@/lib/format';
import { TOP_METRICS, type TopMetric } from '@/types/enums';
import { SectionHeader } from './SectionHeader';
import { AnalyticsEmpty } from './AnalyticsEmpty';

interface TopAccountsSectionProps {
  periodDays: number;
  metric: TopMetric;
  onMetricChange: (m: TopMetric) => void;
}

export function TopAccountsSection({
  periodDays,
  metric,
  onMetricChange,
}: TopAccountsSectionProps) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const top = useTopAccounts({ metric, periodDays, limit: 20 });
  const query = search.trim().replace(/^@/, '').toLocaleLowerCase();
  const rankedRows = (top.data ?? []).map((row, index) => ({ ...row, rank: index + 1 }));
  const matchingRows = rankedRows.filter((row) => row.username.toLocaleLowerCase().includes(query));
  const rows = query || expanded ? matchingRows : matchingRows.slice(0, 5);
  const metricLabels: Record<TopMetric, string> = {
    VIEWS_GROWTH: t('Vues gagnées', 'Views gained'),
    FOLLOWERS_GROWTH: t('Abonnés gagnés', 'Followers gained'),
    AVG_VIEWS_PER_REEL: t('Vues moyennes / reel', 'Average views / reel'),
  };
  const metricDescription =
    metric === 'AVG_VIEWS_PER_REEL'
      ? t(
          'Moyenne des vues totales des reels publiés pendant la période.',
          'Average lifetime views of reels published during the period.',
        )
      : metric === 'FOLLOWERS_GROWTH'
        ? t(
            'Variation du nombre d’abonnés entre les relevés de la période.',
            'Change in follower count between observations in the period.',
          )
        : t(
            'Vues supplémentaires mesurées pendant la période.',
            'Additional views measured during the period.',
          );

  return (
    <section className="min-w-0 space-y-3">
      <SectionHeader
        title={t('Classement des comptes', 'Account ranking')}
        subtitle={t(
          `Jusqu’à 20 comptes ${formatPeriodRange(periodDays)}.`,
          `Up to 20 accounts ${formatPeriodRange(periodDays)}.`,
        )}
        rightSlot={
          <Select value={metric} onValueChange={(v) => onMetricChange(v as TopMetric)}>
            <SelectTrigger
              aria-label={t('Classer les comptes par', 'Rank accounts by')}
              className="h-11 w-full sm:h-9 sm:w-[220px]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TOP_METRICS.map((m) => (
                <SelectItem key={m} value={m}>
                  {metricLabels[m]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      <Tile padded>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-lg text-[12px] leading-5 text-text-3">{metricDescription}</p>
          <div className="relative w-full shrink-0 sm:w-56">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-3"
            />
            <Input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label={t('Rechercher dans ce top 20', 'Search this top 20')}
              placeholder={t('Rechercher dans ce top 20', 'Search this top 20')}
              className="pl-9"
            />
          </div>
        </div>
        {top.isError ? (
          <ErrorInline error={top.error} onRetry={() => void top.refetch()} />
        ) : top.isLoading && !top.data ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-[8px]" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <AnalyticsEmpty
            title={
              query
                ? t('Aucun résultat dans ce classement', 'No matches in this ranking')
                : t('Aucun compte classé', 'No ranked accounts')
            }
            description={
              query
                ? t(
                    'Essayez un autre nom ou effacez la recherche.',
                    'Try another name or clear the search.',
                  )
                : t(
                    'Aucun compte à classer sur cette période.',
                    'No accounts to rank for this period.',
                  )
            }
          />
        ) : (
          <>
            <div className="max-w-full rounded-[10px] border border-line-1">
              <table className="w-full table-fixed text-[13px]">
                <thead className="bg-bg-2 text-left">
                  <tr>
                    <th scope="col" className="w-9 px-2 py-3 font-semibold text-text-3">
                      <span className="sr-only">{t('Rang', 'Rank')}</span>#
                    </th>
                    <th scope="col" className="px-2 py-3 font-semibold text-text-2">
                      {t('Compte', 'Account')}
                    </th>
                    <th
                      scope="col"
                      className="w-28 px-2 py-3 text-right font-semibold text-text-2 sm:w-40"
                    >
                      {metricLabels[metric]}
                    </th>
                    <th
                      scope="col"
                      className="hidden w-28 px-3 py-3 text-right font-semibold text-text-2 sm:table-cell"
                    >
                      {t('Abonnés actuels', 'Current followers')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.accountId}
                      className="border-t border-line-1 transition-colors hover:bg-bg-2"
                    >
                      <td className="px-2 py-2 font-mono text-[11px] text-text-3">{row.rank}</td>
                      <td className="px-2 py-2">
                        <Link
                          to={`/accounts/${row.accountId}`}
                          className="block min-w-0 rounded py-1 font-medium text-text-1 underline-offset-4 [overflow-wrap:anywhere] hover:text-violet hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet"
                        >
                          @{row.username}
                        </Link>
                        <span className="block text-[11px] text-text-3 sm:hidden">
                          {formatNumber(row.latestFollowersCount)} {t('abonnés', 'followers')}
                        </span>
                      </td>
                      <td className="px-2 py-3 text-right font-medium tabular-nums text-text-1">
                        {formatNumber(row.value)}
                      </td>
                      <td className="hidden px-3 py-3 text-right tabular-nums text-text-3 sm:table-cell">
                        {formatNumber(row.latestFollowersCount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-[11px] text-text-3" aria-live="polite">
                {t(
                  `${rows.length} sur ${rankedRows.length} comptes du classement.`,
                  `${rows.length} of ${rankedRows.length} ranked accounts shown.`,
                )}
              </p>
              {!query && rankedRows.length > 5 && (
                <Button
                  variant="secondary"
                  size="sm"
                  aria-expanded={expanded}
                  onClick={() => setExpanded((value) => !value)}
                >
                  {expanded
                    ? t('Réduire', 'Show fewer')
                    : t(
                        `Voir les ${rankedRows.length} comptes`,
                        `Show all ${rankedRows.length} accounts`,
                      )}
                </Button>
              )}
            </div>
          </>
        )}
      </Tile>
    </section>
  );
}
